import React, { useState, useEffect } from 'react';
import { X, Users, Plus, Trash2, Mail, UserPlus } from 'lucide-react';
import { collaboratorService } from '../lib/collaboratorService';
import { Collaborator } from '../types/invitation';
import { createLogger } from '../lib/logger';
import { useSubscription } from '../contexts/SubscriptionContext';
import { UpgradePrompt } from './UpgradePrompt';

const log = createLogger('collaboration');

interface CollaboratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  invitationId: string;
}

const CollaboratorModal: React.FC<CollaboratorModalProps> = ({ 
  isOpen, 
  onClose, 
  invitationId 
}) => {
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingEmail, setAddingEmail] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { canCollaborate } = useSubscription();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  // 載入協作者列表
  const loadCollaborators = async () => {
    if (!invitationId) return;
    
    setLoading(true);
    try {
      const data = await collaboratorService.getCollaborators(invitationId);
      setCollaborators(data);
    } catch (error) {
      log.error('載入協作者失敗:', error);
      setMessage({ type: 'error', text: '載入協作者失敗' });
    } finally {
      setLoading(false);
    }
  };

  // Modal 開啟時載入資料
  useEffect(() => {
    if (isOpen && invitationId) {
      loadCollaborators();
    }
  }, [isOpen, invitationId]);

  // 新增協作者
  const handleAddCollaborator = async () => {
    if (!canCollaborate()) {
      setShowUpgradePrompt(true);
      return;
    }

    if (!addingEmail.trim()) {
      setMessage({ type: 'error', text: '請輸入 Email' });
      return;
    }

    // 基本 email 格式驗證
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(addingEmail.trim())) {
      setMessage({ type: 'error', text: '請輸入有效的 Email 格式' });
      return;
    }

    setIsAdding(true);
    setMessage(null);

    try {
      const result = await collaboratorService.addCollaborator(invitationId, addingEmail.trim());
      
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        setAddingEmail('');
        await loadCollaborators(); // 重新載入列表
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      log.error('新增協作者失敗:', error);
      setMessage({ type: 'error', text: '新增協作者失敗，請稍後再試' });
    } finally {
      setIsAdding(false);
    }
  };

  // 移除協作者
  const handleRemoveCollaborator = async (collaborator: Collaborator) => {
    if (!confirm(`確定要移除協作者「${collaborator.displayName}」嗎？`)) {
      return;
    }

    setMessage(null);
    
    try {
      const result = await collaboratorService.removeCollaborator(collaborator.id);
      
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        await loadCollaborators(); // 重新載入列表
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      log.error('移除協作者失敗:', error);
      setMessage({ type: 'error', text: '移除協作者失敗，請稍後再試' });
    }
  };

  const handleClose = () => {
    setMessage(null);
    setAddingEmail('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* 背景遮罩 */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-wedding-bg rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-wedding-pink/30">
            <h2 className="text-xl font-semibold text-wedding-text-title flex items-center">
              <Users className="h-5 w-5 mr-2 text-wedding-pink" />
              協作者管理
            </h2>
            <button
              onClick={handleClose}
              className="text-wedding-text-body hover:text-wedding-pink transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* 新增協作者 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-wedding-text-title flex items-center">
                <UserPlus className="h-4 w-4 mr-2 text-wedding-pink" />
                邀請協作者
              </h3>
              <div className="flex space-x-2">
                <input
                  type="email"
                  value={addingEmail}
                  onChange={(e) => setAddingEmail(e.target.value)}
                  placeholder="請輸入協作者的 Email"
                  className="flex-1 px-3 py-2 border border-wedding-pink/30 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-pink focus:border-transparent"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCollaborator()}
                />
                <button
                  onClick={handleAddCollaborator}
                  disabled={isAdding || !addingEmail.trim()}
                  className="px-4 py-2 bg-wedding-pink text-white rounded-md hover:bg-wedding-pink/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isAdding ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* 訊息提示 */}
            {message && (
              <div className={`rounded-md p-3 ${
                message.type === 'success' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <p className={`text-sm ${
                  message.type === 'success' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {message.text}
                </p>
              </div>
            )}

            {/* 協作者列表 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-wedding-text-title">
                目前協作者 ({collaborators.length})
              </h3>
              
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-wedding-pink border-t-transparent rounded-full animate-spin" />
                </div>
              ) : collaborators.length === 0 ? (
                <div className="text-center py-8 text-wedding-text-body">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>尚無協作者</p>
                  <p className="text-sm">邀請其他人一起管理婚禮規劃吧！</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {collaborators.map((collaborator) => (
                    <div key={collaborator.id} className="flex items-center justify-between p-3 border border-wedding-pink/20 rounded-md">
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 rounded-full bg-wedding-pink flex items-center justify-center text-white font-medium text-sm">
                          {collaborator.displayName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-wedding-text-title text-sm">
                            {collaborator.displayName}
                          </p>
                          <p className="text-xs text-wedding-text-body flex items-center">
                            <Mail className="h-3 w-3 mr-1" />
                            {collaborator.email}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveCollaborator(collaborator)}
                        className="text-red-600 hover:text-red-800 transition-colors p-1"
                        title="移除協作者"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end p-6 border-t border-wedding-pink/30">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-wedding-pink text-white rounded-md hover:bg-wedding-pink/90 transition-colors"
            >
              完成
            </button>
          </div>
        </div>
      </div>
      <UpgradePrompt
        open={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        message="協作功能需要標準方案或以上。升級方案以邀請協作者。"
      />
    </>
  );
};

export default CollaboratorModal;